import { useEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Camera,
  Download,
  Image as ImageIcon,
  Minus,
  Plus,
  RotateCcw,
  Share2,
  X,
  Check,
} from 'lucide-react';
import type { AvatarTemplate, ImageTransform } from '../../types/avatar';
import { AvatarCanvas } from './AvatarCanvas';
import { CameraCaptureModal } from './CameraCaptureModal';

interface AvatarEditorModalProps {
  isOpen: boolean;
  template: AvatarTemplate | null;
  onClose: () => void;
}

const defaultTransform: ImageTransform = {
  zoom: 1.0,
  panX: 0,
  panY: 0,
  rotation: 0,
};

interface AvatarEditorContentProps {
  template: AvatarTemplate;
  onClose: () => void;
}

function AvatarEditorContent({ template, onClose }: AvatarEditorContentProps) {
  const [userImage, setUserImage] = useState<HTMLImageElement | null>(null);
  const [transform, setTransform] = useState<ImageTransform>(defaultTransform);
  const [logoMode, setLogoMode] = useState<'text' | 'logo'>(() =>
    template.styleVariant.includes('logo') ? 'logo' : 'text'
  );
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasElementRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Handle escape key to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isCameraOpen) {
          setIsCameraOpen(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isCameraOpen, onClose]);

  // Handle file select
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setUserImage(img);
        setTransform(defaultTransform);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Zoom controls
  const handleZoomChange = (value: number) => {
    setTransform((prev) => ({
      ...prev,
      zoom: Math.min(3.5, Math.max(0.4, value)),
    }));
  };

  const handleZoomStep = (delta: number) => {
    setTransform((prev) => ({
      ...prev,
      zoom: Math.min(3.5, Math.max(0.4, Number((prev.zoom + delta).toFixed(2)))),
    }));
  };

  // Pan controls
  const handlePanStep = (dx: number, dy: number) => {
    setTransform((prev) => ({
      ...prev,
      panX: prev.panX + dx,
      panY: prev.panY + dy,
    }));
  };

  const handleReset = () => {
    setTransform(defaultTransform);
  };

  // Download high-res image
  const handleDownload = () => {
    const canvas = canvasElementRef.current;
    if (!canvas || !template) return;

    const dataUrl = canvas.toDataURL('image/png', 1.0);
    const link = document.createElement('a');
    link.download = `traiano-55789-${template.id}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Native share
  const handleShare = async () => {
    const canvas = canvasElementRef.current;
    if (!canvas || !template) return;

    try {
      if (navigator.share) {
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const file = new File([blob], `traiano-55789-${template.id}.png`, {
            type: 'image/png',
          });

          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: 'Meu Avatar Traiano 55789',
              text: 'Acabei de criar meu avatar com o Traiano 55789! Crie o seu também em traiano.com.br/avatar',
              files: [file],
            });
          } else {
            await navigator.share({
              title: 'Meu Avatar Traiano 55789',
              text: 'Acabei de criar meu avatar com o Traiano 55789! Crie o seu também em traiano.com.br/avatar',
              url: window.location.href,
            });
          }
        }, 'image/png');
      } else {
        handleDownload();
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch {
      // User cancelled share dialog or unsupported
    }
  };

  return (
    <>
      {/* BACKDROP */}
      <div className="avatar-modal-backdrop" onClick={onClose}>
        {/* PANEL */}
        <div className="avatar-modal-panel" onClick={(e) => e.stopPropagation()}>

          {/* ─── HEADER (Clean Light Mode) ─── */}
          <div className="avatar-modal-header">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-md bg-[#06194d]/8 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-[#06194d]">
                  {template.category === 'avatar' ? 'Foto de Perfil' : 'Card para Redes'}
                </span>
                <span className="font-display text-sm font-bold text-[#06194d]">
                  {template.name}
                </span>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#f1f4f9] text-[#06194d] hover:bg-[#e2eaf5] transition-colors"
                aria-label="Fechar editor"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="mt-2 text-left text-xs text-[#5a6b88] font-body">
              {userImage
                ? 'Arraste a foto para enquadrar • Use o controle de zoom abaixo'
                : 'Escolha uma foto da galeria ou tire uma com a câmera para começar'}
            </p>
          </div>

          {/* ─── BODY (scrollable) ─── */}
          <div className="avatar-modal-body">
            {/* Canvas */}
            <AvatarCanvas
              template={template}
              userImage={userImage}
              transform={transform}
              logoMode={logoMode}
              onTransformChange={setTransform}
              onCanvasReady={(canvas) => {
                canvasElementRef.current = canvas;
              }}
            />

            {/* Brand Style Selector (Text 55789 vs Logo Oficial) */}
            <div className="mt-3 flex items-center justify-between rounded-xl bg-[#f4f7fb] p-1.5 border border-[#d9e0ec]">
              <span className="pl-2 text-xs font-bold uppercase tracking-wider text-[#5a6b88] font-display">
                Versão da Marca
              </span>
              <div className="flex items-center rounded-lg bg-[#e2eaf5]/80 p-0.5">
                <button
                  type="button"
                  onClick={() => setLogoMode('text')}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                    logoMode === 'text'
                      ? 'bg-white text-[#06194d] shadow-xs'
                      : 'text-[#5a6b88] hover:text-[#06194d]'
                  }`}
                >
                  <span>Texto 55789</span>
                </button>
                <button
                  type="button"
                  onClick={() => setLogoMode('logo')}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                    logoMode === 'logo'
                      ? 'bg-[#06194d] text-white shadow-xs'
                      : 'text-[#5a6b88] hover:text-[#06194d]'
                  }`}
                >
                  <span>Logo Oficial</span>
                </button>
              </div>
            </div>

            {/* If NO user image: Primary Action Buttons */}
            {!userImage ? (
              <div className="mt-3 flex flex-col gap-2.5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="avatar-file-upload"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-[#06194d] px-4 font-body text-sm font-bold text-white shadow-md hover:bg-[#092a82] active:scale-98 transition-all cursor-pointer"
                  >
                    <ImageIcon className="h-4 w-4 text-[#16a53a]" />
                    <span>Escolher Foto</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsCameraOpen(true)}
                    className="flex min-h-[46px] items-center justify-center gap-2 rounded-xl border-2 border-[#06194d]/15 bg-white px-4 font-body text-sm font-bold text-[#06194d] shadow-xs hover:bg-[#f4f7fb] hover:border-[#06194d]/30 active:scale-98 transition-all cursor-pointer"
                  >
                    <Camera className="h-4 w-4 text-[#123ee0]" />
                    <span>Tirar Foto</span>
                  </button>
                </div>
              </div>
            ) : (
              /* Controls when image is loaded */
              <div className="mt-2.5 flex flex-col gap-2">
                {/* Zoom range slider */}
                <div className="flex items-center gap-2.5 rounded-xl bg-white px-3 py-2 shadow-xs border border-[#d9e0ec]">
                  <button
                    type="button"
                    onClick={() => handleZoomStep(-0.1)}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#f1f4f9] text-[#06194d] hover:bg-[#06194d] hover:text-white transition-colors cursor-pointer"
                    aria-label="Diminuir zoom"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>

                  <input
                    type="range"
                    min="0.4"
                    max="3.0"
                    step="0.05"
                    value={transform.zoom}
                    onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
                    className="h-1.5 flex-1 cursor-pointer"
                    aria-label="Controle de zoom"
                  />

                  <button
                    type="button"
                    onClick={() => handleZoomStep(0.1)}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#f1f4f9] text-[#06194d] hover:bg-[#06194d] hover:text-white transition-colors cursor-pointer"
                    aria-label="Aumentar zoom"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Pan step buttons + reset (compact row) */}
                <div className="flex items-center justify-center gap-1.5">
                  {[
                    { icon: ArrowLeft, dx: -30, dy: 0, label: 'Mover para esquerda' },
                    { icon: ArrowUp, dx: 0, dy: -30, label: 'Mover para cima' },
                    { icon: ArrowDown, dx: 0, dy: 30, label: 'Mover para baixo' },
                    { icon: ArrowRight, dx: 30, dy: 0, label: 'Mover para direita' },
                  ].map(({ icon: Icon, dx, dy, label }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => handlePanStep(dx, dy)}
                      className="grid h-8 w-8 place-items-center rounded-lg bg-white text-[#06194d] shadow-xs border border-[#d9e0ec] hover:bg-[#f1f4f9] active:scale-95 transition-all cursor-pointer"
                      aria-label={label}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={handleReset}
                    className="grid h-8 w-8 place-items-center rounded-lg bg-white text-[#06194d] shadow-xs border border-[#d9e0ec] hover:bg-[#f1f4f9] active:scale-95 transition-all cursor-pointer"
                    title="Centralizar e resetar enquadramento"
                    aria-label="Resetar enquadramento"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Trocar Foto / Tirar Foto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex h-9 items-center justify-center gap-1.5 rounded-lg border border-[#d9e0ec] bg-white px-3 font-body text-xs font-bold text-[#06194d] hover:bg-[#f1f4f9] active:scale-98 transition-colors cursor-pointer"
                  >
                    <ImageIcon className="h-3.5 w-3.5 text-[#16a53a]" />
                    <span>Trocar Foto</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsCameraOpen(true)}
                    className="flex h-9 items-center justify-center gap-1.5 rounded-lg border border-[#d9e0ec] bg-white px-3 font-body text-xs font-bold text-[#06194d] hover:bg-[#f1f4f9] active:scale-98 transition-colors cursor-pointer"
                  >
                    <Camera className="h-3.5 w-3.5 text-[#123ee0]" />
                    <span>Tirar Outra</span>
                  </button>
                </div>
              </div>
            )}

            {/* Privacy notice */}
            <p className="mt-3 text-center text-xs text-[#71809a] leading-tight font-body">
              Sua foto não sai do seu aparelho: tudo é processado com segurança no seu navegador.
            </p>
          </div>

          {/* ─── FOOTER (pinned CTA) ─── */}
          {userImage && (
            <div className="avatar-modal-footer">
              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[#06194d] px-4 font-body text-sm font-bold text-white shadow-md hover:bg-[#092a82] active:scale-98 transition-all cursor-pointer"
                >
                  <Download className="h-4 w-4" />
                  <span>Salvar Imagem</span>
                </button>

                <button
                  type="button"
                  onClick={handleShare}
                  className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[#16a53a] px-4 font-body text-sm font-bold text-white shadow-md hover:bg-[#149334] active:scale-98 transition-all cursor-pointer"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
                  <span>{copied ? 'Baixada!' : 'Compartilhar'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Embedded Live Camera Modal */}
      <CameraCaptureModal
        isOpen={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onCapture={(capturedImage) => {
          setUserImage(capturedImage);
          setTransform(defaultTransform);
        }}
      />
    </>
  );
}

export function AvatarEditorModal({ isOpen, template, onClose }: AvatarEditorModalProps) {
  if (!isOpen || !template) return null;

  return <AvatarEditorContent key={template.id} template={template} onClose={onClose} />;
}
